import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { AlertTriangle, Check, ChevronDown, Download, FileText, Minus, RefreshCw, Search } from 'lucide-react';
import { usePermissions } from 'hooks/usePermissions';
import SearchableSelect from 'components/common/SearchableSelect';
import { nomeDeExibicao, nomeParaCena } from 'features/comercial/components/RankingComercialTab';
import service from 'features/comercial/services/comissaoReguaService';
import { mesCorrente } from 'features/comercial/components/comissao/ComissaoReguaWorkspace';
import { Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';

/**
 * Subaba "Minhas vendas": o que a vendedora vendeu no mês, o que já está instalado e quanto isso
 * rende de comissão.
 *
 * Substitui o painel que as vendedoras mantinham à mão. Nada aqui é digitado nem calculado: os
 * valores são os da régua de comissão, recortados para o vendedor vinculado ao usuário — o backend
 * decide o recorte. Quem tem `comercial.view` (o supervisor) escolhe o vendedor, e quem edita metas
 * também escolhe o usuário vinculado. As decisões estão em `docs/COMERCIAL_DECISOES.md`, seção 1.
 */

// A régua nova começa em setembro/2026; antes disso não há o que mostrar.
const PRIMEIRO_MES = '2026-09';

const reais = (valor) => Number(valor || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
// Taxa de comissão: 0,08 vira "8%"; 0,125 vira "12,5%".
const taxa = (fracao) => `${Number((Number(fracao || 0) * 100).toFixed(2)).toLocaleString('pt-BR')}%`;
// Atingimento sempre para BAIXO: 49,9% exibido como "50%" diria que a faixa foi atingida.
const atingimentoDe = (resumo) => Math.floor(Number(resumo?.atingimento || 0) * 100 + 1e-9);
const plural = (quantidade, um, varios) => `${quantidade} ${quantidade === 1 ? um : varios}`;
// Só a data: `new Date('2026-10-05')` é meia-noite UTC, e em Santarém viraria o dia 4.
const dia = (valor) => (valor ? String(valor).slice(0, 10).split('-').reverse().join('/') : '—');
// De quando é o dado. Hoje, só a hora; de outro dia (a última leitura boa com o IXC fora), a data.
const quandoFoiLido = (iso) => {
  const quando = new Date(iso);
  if (Number.isNaN(quando.getTime())) return '';
  const hora = quando.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });

  return quando.toDateString() === new Date().toDateString()
    ? `Atualizado às ${hora}`
    : `Atualizado em ${quando.toLocaleDateString('pt-BR')} às ${hora}`;
};
const diaEHora = (iso) => {
  const quando = new Date(iso);
  return Number.isNaN(quando.getTime())
    ? ''
    : `${quando.toLocaleDateString('pt-BR')} às ${quando.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}`;
};

// "ANDRESA ROSS VEND-INT" é o cadastro do IXC; na tela, o nome da pessoa.
const nomeDoVendedor = (nome) => nomeParaCena(nomeDeExibicao(nome || ''));
const nomeDoCliente = (contrato) => (contrato.cliente_nome ? nomeParaCena(contrato.cliente_nome) : 'Nome indisponível');
const normalizarBusca = (valor) => String(valor ?? '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLocaleLowerCase('pt-BR').trim();
const correspondeABusca = (item, busca) => {
  const termo = normalizarBusca(busca);
  if (!termo) return true;
  if (/^\d+$/.test(termo)) return [item.cliente_id, item.contrato_id].some((valor) => String(valor ?? '') === termo);
  return normalizarBusca(item.cliente_nome).includes(termo);
};

const MOTIVOS = {
  sem_vinculo: 'Seu usuário ainda não está vinculado a um vendedor. Fale com o seu gestor.',
  fora_da_equipe: 'Este vendedor não faz parte do time interno desta competência.',
  sem_regua: 'Não há régua de comissão nesta competência.',
};

const caixa = 'rounded-xl border border-[color:var(--border)] bg-[color:var(--surface)]';
const campo = 'min-h-11 rounded-md border border-[color:var(--border)] bg-[color:var(--surface)] px-3 py-2 text-sm text-[color:var(--text-primary)] focus:outline-none focus:ring-2 focus:ring-[color:var(--accent)]';
const botaoSecundario = `${campo} inline-flex items-center gap-2 font-medium transition hover:bg-[color:var(--surface-muted)] disabled:cursor-not-allowed disabled:opacity-60`;
const botaoDeTexto = 'inline-flex min-h-11 items-center rounded-md px-2 text-sm font-medium text-[color:var(--accent)] underline-offset-4 hover:underline focus:outline-none focus:ring-2 focus:ring-[color:var(--accent)] disabled:cursor-not-allowed disabled:opacity-60';
// Texto de status usa `-strong` sobre `-chip`: os tokens `-fg` rendem menos de 3:1 no tema claro
// (ver o comentário deles em `index.css`).
const AVISO = 'border-[color:var(--status-warning-border)] bg-[color:var(--status-warning-chip)] text-[color:var(--status-warning-strong)]';
const ERRO = 'border-[color:var(--status-danger-border)] bg-[color:var(--status-danger-chip)] text-[color:var(--status-danger-strong)]';

const Instalado = ({ valor }) => {
  if (valor === null || valor === undefined) return <span className="text-[color:var(--text-muted)]">—</span>;

  return valor
    ? <span className="inline-flex items-center gap-1 font-medium text-[color:var(--status-good-strong)]"><Check className="h-4 w-4" aria-hidden="true" />Instalado</span>
    : <span className="inline-flex items-center gap-1 text-[color:var(--text-muted)]"><Minus className="h-4 w-4" aria-hidden="true" />Não instalado</span>;
};

/**
 * A régua do mês: onde a vendedora está, quanto já rendeu e quanto falta para a próxima faixa.
 *
 * É o objeto do domínio, e por isso é desenhado: quatro cartões de mesmo peso deixavam o total e a
 * faixa — as duas coisas que ela abre a tela para ver — como os elementos mais fracos da página.
 * O trilho vai um pouco além da última faixa para a marca dela não encostar na borda.
 */
const Regua = ({ resumo, faixas, simulacao, fechado, metaDiaria }) => {
  const atingido = atingimentoDe(resumo);
  const topo = Math.max(...faixas.map((faixa) => faixa.percentual_meta * 1.08), atingido, 100);
  const posicao = (percentual) => `${Math.min(100, (percentual / topo) * 100)}%`;
  const proxima = simulacao[0] || null;
  const estimativa = new Map(simulacao.map((faixa) => [faixa.percentual_meta, faixa]));
  const atual = [...faixas].reverse().find((faixa) => !estimativa.has(faixa.percentual_meta)) || null;

  return (
    <section className={`${caixa} overflow-hidden`}>
      <div className="flex flex-col gap-6 p-5 lg:flex-row lg:items-start lg:gap-10">
        {/* No celular o dinheiro vem primeiro: é a resposta que cabe na primeira tela. */}
        <div className="lg:order-last lg:w-64 lg:text-right">
          <p className="text-sm text-[color:var(--text-muted)]">{fechado ? 'Comissão do mês' : 'Comissão até agora'}</p>
          <p className="mt-1 text-4xl font-bold tabular-nums tracking-tight text-[color:var(--text-primary)]">{reais(resumo.liquido)}</p>
          <dl className="mt-2 space-y-0.5 text-xs text-[color:var(--text-muted)]">
            <div className="flex justify-between gap-3 lg:justify-end">
              <dt>Vendas, {taxa(resumo.taxa_comissao)} sobre {reais(resumo.base_vendas)}</dt>
              <dd className="tabular-nums text-[color:var(--text-primary)]">{reais(resumo.comissao_vendas)}</dd>
            </div>
            <div className="flex justify-between gap-3 lg:justify-end">
              <dt>{plural(resumo.upsells_quantidade, 'up-sell', 'up-sells')}</dt>
              <dd className="tabular-nums text-[color:var(--text-primary)]">{reais(resumo.comissao_upsell)}</dd>
            </div>
          </dl>
        </div>

        <div className="min-w-0 flex-1">
          <p className="text-[color:var(--text-primary)]">
            <span className="text-2xl font-bold tabular-nums">{resumo.vendas_elegiveis}</span>
            <span className="text-sm"> de {resumo.meta_vendas} vendas, {atingido}% da meta</span>
          </p>

          <div className="relative mt-3 pb-7">
            <div
              role="progressbar"
              aria-label="Progresso na régua de comissão"
              aria-valuemin={0}
              aria-valuemax={Math.round(topo)}
              aria-valuenow={atingido}
              aria-valuetext={`${atingido}% da meta${atual ? `, faixa de ${taxa(atual.taxa)}` : ''}`}
              // Com contorno: no tema escuro `--surface-muted` quase não se distingue do cartão, e
              // as marcas das faixas pareciam soltas no ar.
              className="h-3 overflow-hidden rounded-full bg-[color:var(--surface-muted)] ring-1 ring-inset ring-[color:var(--border)]"
            >
              <div className="h-full rounded-full bg-[color:var(--accent)] transition-[width] duration-500" style={{ width: posicao(atingido) }} />
            </div>
            {faixas.map((faixa) => {
              const atingida = !estimativa.has(faixa.percentual_meta);
              return (
                <div key={faixa.percentual_meta} className="absolute top-0 flex -translate-x-1/2 flex-col items-center" style={{ left: posicao(faixa.percentual_meta) }} aria-hidden="true">
                  <span className="h-5 w-px bg-[color:var(--text-muted)]" />
                  <span className={`mt-1 text-xs tabular-nums ${atingida ? 'font-semibold text-[color:var(--text-primary)]' : 'text-[color:var(--text-muted)]'}`}>{Number(faixa.percentual_meta)}%</span>
                </div>
              );
            })}
          </div>

          <p className="mt-1 text-sm text-[color:var(--text-primary)]">
            {fechado && `Fechou em ${atingido}% da meta, com comissão de ${taxa(resumo.taxa_comissao)} sobre as vendas.`}
            {!fechado && proxima && (
              <>
                <strong>Faltam {plural(proxima.vendas_que_faltam, 'venda', 'vendas')}</strong> para a faixa de {taxa(proxima.taxa)}: a comissão iria para cerca de {reais(proxima.comissao_estimada)}.
              </>
            )}
            {!fechado && !proxima && atual && `Você já está na última faixa da régua: comissão de ${taxa(atual.taxa)}.`}
          </p>
        </div>
      </div>

      {metaDiaria && (
        <dl className="grid grid-cols-2 border-t border-[color:var(--border)] bg-[color:var(--surface-muted)] sm:grid-cols-4">
          {[
            ['Planejada hoje', metaDiaria.meta_planejada_hoje],
            ['Necessária hoje', metaDiaria.meta_necessaria_hoje],
            ['Realizado hoje', metaDiaria.vendas_hoje],
            ['Faltam hoje', metaDiaria.faltam_hoje],
          ].map(([rotulo, valor]) => (
            <div key={rotulo} className="border-b border-[color:var(--border)] px-5 py-3 even:border-l sm:border-b-0 sm:border-l sm:first:border-l-0">
              <dt className="text-xs font-medium text-[color:var(--text-muted)]">{rotulo}</dt>
              <dd className="mt-0.5 text-xl font-bold tabular-nums text-[color:var(--text-primary)]">{Number(valor || 0)}</dd>
            </div>
          ))}
        </dl>
      )}

      {!fechado && faixas.length > 0 && (
        <div className="border-t border-[color:var(--border)]">
          <div className="px-5 pt-4">
            <h3 className="font-semibold text-[color:var(--text-primary)]">Faixas da régua</h3>
            {proxima && (
              <p className="mt-1 text-sm text-[color:var(--text-muted)]">
                Os valores das próximas faixas são estimativas: somam às suas vendas do mês as que faltam, pelo seu ticket médio ({reais(proxima.ticket_medio)}).
              </p>
            )}
          </div>
          <ol className="mt-2">
            {faixas.map((faixa) => {
              const falta = estimativa.get(faixa.percentual_meta);
              const ehAtual = atual?.percentual_meta === faixa.percentual_meta;
              return (
                <li key={faixa.percentual_meta} className={`flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1 border-t border-[color:var(--border)] px-5 py-3 text-sm first:border-t-0 ${ehAtual ? 'bg-[color:var(--surface-muted)]' : ''}`}>
                  <span className="text-[color:var(--text-primary)]">
                    A partir de {Number(faixa.percentual_meta)}% da meta: <strong>comissão de {taxa(faixa.taxa)}</strong>
                  </span>
                  {falta ? (
                    <span className="tabular-nums text-[color:var(--text-muted)]">
                      faltam {plural(falta.vendas_que_faltam, 'venda', 'vendas')}, cerca de <span className="text-[color:var(--text-primary)]">{reais(falta.comissao_estimada)}</span>
                    </span>
                  ) : (
                    <span className="inline-flex items-center gap-1 font-medium text-[color:var(--status-good-strong)]">
                      <Check className="h-4 w-4" aria-hidden="true" />
                      {ehAtual ? 'Sua faixa hoje' : 'Atingida'}
                    </span>
                  )}
                </li>
              );
            })}
          </ol>
        </div>
      )}
    </section>
  );
};

const TooltipMetaDiaria = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  const dados = payload[0]?.payload || {};

  return (
    <div className="rounded-lg border border-[color:var(--border)] bg-[color:var(--surface-strong)] px-3 py-2 text-xs shadow-lg">
      <p className="font-semibold text-[color:var(--text-primary)]">Dia {label}</p>
      <p className="mt-1 text-[color:var(--text-muted)]">Planejada: <strong className="tabular-nums text-[color:var(--text-primary)]">{dados.meta_planejada}</strong></p>
      {dados.meta_necessaria !== null && <p className="text-[color:var(--text-muted)]">Necessária: <strong className="tabular-nums text-[color:var(--text-primary)]">{dados.meta_necessaria}</strong></p>}
      {dados.vendas !== null && <p className="text-[color:var(--text-muted)]">Realizado: <strong className="tabular-nums text-[color:var(--text-primary)]">{dados.vendas}</strong></p>}
    </div>
  );
};

const GraficoMetaDiaria = ({ metaDiaria }) => {
  const dados = (metaDiaria?.dias || []).map((item) => ({
    ...item,
    dia: String(item.data || '').slice(8, 10),
  }));
  if (dados.length === 0) return null;

  return (
    <section className={`${caixa} overflow-hidden`} aria-label="Ritmo diário do mês">
      <div className="flex flex-wrap items-end justify-between gap-3 border-b border-[color:var(--border)] px-5 py-4">
        <div>
          <h3 className="font-semibold text-[color:var(--text-primary)]">Ritmo diário do mês</h3>
          <p className="mt-1 text-sm text-[color:var(--text-muted)]">A meta usa o planejado nos dias passados e o necessário de hoje até o fim. O realizado segue o Ranking e inclui pré-contratos.</p>
        </div>
        <div className="flex gap-4 text-xs font-medium text-[color:var(--text-muted)]" aria-label="Legenda">
          <span className="inline-flex items-center gap-1.5"><span className="h-2.5 w-2.5 rounded-sm bg-[color:var(--accent)]" />Realizado</span>
          <span className="inline-flex items-center gap-1.5"><span className="h-2.5 w-2.5 rounded-sm bg-violet-500" />Meta do dia</span>
        </div>
      </div>
      <div className="overflow-x-auto px-2 pb-3 pt-4 sm:px-4">
        <div className="h-72 min-w-[820px]" aria-hidden="true">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={dados} margin={{ top: 8, right: 8, left: -18, bottom: 0 }} barGap={1}>
              <CartesianGrid vertical={false} stroke="var(--border)" strokeDasharray="3 3" />
              <XAxis dataKey="dia" tickLine={false} axisLine={false} tick={{ fill: 'var(--text-muted)', fontSize: 11 }} />
              <YAxis allowDecimals={false} tickLine={false} axisLine={false} tick={{ fill: 'var(--text-muted)', fontSize: 11 }} />
              <Tooltip content={<TooltipMetaDiaria />} cursor={{ fill: 'var(--surface-muted)' }} />
              <Bar dataKey="vendas" name="Realizado" fill="var(--accent)" radius={[3, 3, 0, 0]} maxBarSize={16} />
              <Bar dataKey="meta_diaria" name="Meta do dia" fill="#8b5cf6" radius={[3, 3, 0, 0]} maxBarSize={16} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>
      <table className="sr-only">
        <caption>Vendas realizadas e metas por dia do mês</caption>
        <thead><tr><th scope="col">Data</th><th scope="col">Realizado</th><th scope="col">Meta do dia</th></tr></thead>
        <tbody>
          {dados.map((dia) => (
            <tr key={dia.data}>
              <th scope="row">{dia.data}</th>
              <td>{dia.vendas === null ? 'Ainda não ocorreu' : dia.vendas}</td>
              <td>{dia.meta_diaria}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </section>
  );
};

/**
 * Uma lista que pode ser minimizada, com rolagem própria.
 *
 * `<details>` nativo: abre e fecha sem estado nenhum, funciona por teclado e o leitor de tela anuncia
 * "expandido" ou "recolhido" sozinho. A altura é limitada só do tablet para cima — no celular uma
 * rolagem dentro da outra prende o dedo, e a lista já pode ser minimizada.
 */
const Recolhivel = ({ titulo, nota, children }) => (
  <section className={`${caixa} overflow-hidden`}>
    <details open className="group">
      <summary className="flex cursor-pointer list-none items-start justify-between gap-3 p-4 focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-[color:var(--accent)] [&::-webkit-details-marker]:hidden">
        <div>
          <h3 className="font-semibold text-[color:var(--text-primary)]">{titulo}</h3>
          {nota && <p className="mt-1 text-sm text-[color:var(--text-muted)]">{nota}</p>}
        </div>
        <ChevronDown className="mt-1 h-4 w-4 shrink-0 text-[color:var(--text-muted)] transition-transform group-open:rotate-180" aria-hidden="true" />
      </summary>
      <div className="border-t border-[color:var(--border)] sm:max-h-[26rem] sm:overflow-auto">{children}</div>
    </details>
  </section>
);

/**
 * Uma lista de contratos. `comComissao` é falso nos grupos que não contam.
 *
 * Duas formas do mesmo dado: tabela do tablet para cima e, no celular, uma lista empilhada. A
 * tabela precisa de 640 px, e numa tela de 390 px a comissão e a instalação — as duas colunas que
 * a vendedora abre a tela para ver — ficavam fora, a um arrasto de lado em toda linha.
 */
const Contratos = ({ titulo, nota, contratos, comComissao = true, buscando = false }) => (
  <Recolhivel titulo={`${titulo} (${contratos.length})`} nota={nota}>
    {contratos.length === 0 ? (
      <p className="p-4 text-sm text-[color:var(--text-muted)]">{buscando ? 'Nenhum resultado para esta busca.' : 'Nenhuma venda neste mês ainda.'}</p>
    ) : (
      <>
        <ul className="sm:hidden">
          {contratos.map((contrato) => (
            <li key={contrato.contrato_id} className="border-t border-[color:var(--border)] px-4 py-3 first:border-t-0">
              <div className="flex items-baseline justify-between gap-3 text-sm">
                <span className="min-w-0 font-medium text-[color:var(--text-primary)]">{nomeDoCliente(contrato)}</span>
                <span className="shrink-0 font-semibold tabular-nums text-[color:var(--text-primary)]">
                  {comComissao ? reais(contrato.comissao) : reais(contrato.valor)}
                </span>
              </div>
              {/* Cada coisa no seu lugar em toda linha: o nome do plano varia muito de tamanho, e
                  deixar tudo quebrar junto punha a instalação ora à direita, ora embaixo. */}
              <div className="mt-1 flex items-center justify-between gap-3 text-xs text-[color:var(--text-muted)]">
                <span className="min-w-0 truncate">{contrato.plano}</span>
                <span className="shrink-0"><Instalado valor={contrato.instalado} /></span>
              </div>
              <p className="mt-0.5 text-xs tabular-nums text-[color:var(--text-muted)]">
                cliente {contrato.cliente_id} · contrato {contrato.contrato_id} · {comComissao ? `${reais(contrato.valor)} em ${dia(contrato.data_venda)}` : dia(contrato.data_venda)}
              </p>
            </li>
          ))}
        </ul>

        <table className="hidden w-full text-sm sm:table">
          {/* `--surface-strong`, e não `--surface`: no tema escuro a superfície comum é translúcida, e
              as linhas apareciam por trás do cabeçalho preso. */}
          <thead className="sticky top-0 z-10 bg-[color:var(--surface-strong)] text-left text-xs text-[color:var(--text-muted)] shadow-[0_1px_0_var(--border)]">
            <tr>
              <th scope="col" className="px-4 py-3 font-medium">Cliente</th>
              <th scope="col" className="px-3 py-3 font-medium">Plano</th>
              <th scope="col" className="px-3 py-3 text-right font-medium">Valor</th>
              {comComissao && <th scope="col" className="px-3 py-3 text-right font-medium">Comissão</th>}
              <th scope="col" className="px-3 py-3 pl-8 font-medium">Data</th>
              <th scope="col" className="px-4 py-3 font-medium">Instalação</th>
            </tr>
          </thead>
          <tbody>
            {contratos.map((contrato) => (
              <tr key={contrato.contrato_id} className="border-t border-[color:var(--border)] first:border-t-0">
                <td className="px-4 py-3">
                  <span className="font-medium text-[color:var(--text-primary)]">{nomeDoCliente(contrato)}</span>
                  <span className="block text-xs tabular-nums text-[color:var(--text-muted)]">cliente {contrato.cliente_id} · contrato {contrato.contrato_id}</span>
                </td>
                <td className="px-3 py-3 text-[color:var(--text-primary)]">{contrato.plano}</td>
                <td className="px-3 py-3 text-right tabular-nums text-[color:var(--text-primary)]">{reais(contrato.valor)}</td>
                {comComissao && (
                  <td className="px-3 py-3 text-right tabular-nums text-[color:var(--text-primary)]">
                    {contrato.comissao === undefined || contrato.comissao === null ? '—' : reais(contrato.comissao)}
                  </td>
                )}
                <td className="px-3 py-3 pl-8 tabular-nums text-[color:var(--text-muted)]">{dia(contrato.data_venda)}</td>
                <td className="px-4 py-3"><Instalado valor={contrato.instalado} /></td>
              </tr>
            ))}
          </tbody>
        </table>
      </>
    )}
  </Recolhivel>
);

/**
 * Quem acessa a subaba como este vendedor. Só para quem edita metas.
 *
 * Fica no cabeçalho do bloco, e não na barra de filtros: é uma gravação, e ao lado do seletor de
 * vendedor parecia mais um filtro. Trocar exige "Salvar", e remover pede confirmação — um toque
 * errado tirava o acesso de uma vendedora, que só descobriria ao abrir a tela vazia.
 */
const Acesso = ({ vendedor, usuarios, onVincular, onDesvincular }) => {
  const [modo, setModo] = useState(null);
  const [escolhido, setEscolhido] = useState(null);
  const [gravando, setGravando] = useState(false);
  const [aviso, setAviso] = useState('');
  const atual = usuarios.find((usuario) => usuario.id === vendedor.user_id) || null;
  const nome = nomeDoVendedor(vendedor.nome);
  const rotuloDoAtual = atual?.label || (vendedor.user_id ? 'usuário inativo ou de outra empresa' : 'ninguém');

  const fechar = () => { setModo(null); setEscolhido(null); };
  const gravar = async (acao, mensagem) => {
    setGravando(true);
    setAviso('');
    try {
      await acao();
      setAviso(mensagem);
      fechar();
    } catch (falha) {
      // Quem chamou já mostrou o erro; aqui só não se anuncia um sucesso que não houve.
    } finally {
      setGravando(false);
    }
  };

  return (
    <div className="w-full text-sm sm:ml-auto sm:w-auto">
      {modo === null && (
        <div className="flex flex-wrap items-center gap-x-2">
          <span className="text-[color:var(--text-muted)]">Acesso: <strong className="text-[color:var(--text-primary)]">{rotuloDoAtual}</strong></span>
          <button type="button" className={botaoDeTexto} onClick={() => { setAviso(''); setModo('alterar'); }}>Alterar</button>
          {vendedor.user_id && <button type="button" className={botaoDeTexto} onClick={() => { setAviso(''); setModo('remover'); }}>Remover acesso</button>}
        </div>
      )}

      {modo === 'alterar' && (
        <div className="flex flex-wrap items-center gap-2">
          <SearchableSelect
            className="w-full sm:w-64"
            options={usuarios}
            value={escolhido}
            onChange={setEscolhido}
            placeholder="Buscar usuário"
            ariaLabel={`Usuário que acessa como ${nome}`}
          />
          <button
            type="button"
            className={botaoSecundario}
            disabled={gravando || !escolhido || escolhido.id === vendedor.user_id}
            onClick={() => gravar(() => onVincular(escolhido.id), `Acesso gravado: ${escolhido.label} vê as vendas de ${nome}.`)}
          >
            Salvar acesso
          </button>
          <button type="button" className={botaoDeTexto} onClick={fechar}>Cancelar</button>
        </div>
      )}

      {modo === 'remover' && (
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-[color:var(--text-primary)]">Remover o acesso de {rotuloDoAtual} às vendas de {nome}?</span>
          <button type="button" className={botaoSecundario} disabled={gravando} onClick={() => gravar(onDesvincular, `Acesso removido: ninguém vê as vendas de ${nome} por esta subaba.`)}>
            Remover acesso
          </button>
          <button type="button" className={botaoDeTexto} onClick={fechar}>Cancelar</button>
        </div>
      )}

      <p role="status" className="text-[color:var(--status-good-strong)]">{aviso}</p>
    </div>
  );
};

const Bloco = ({ bloco, acesso, onTentarDeNovo, busca }) => {
  const cabecalho = (selo) => (
    <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
      <h2 className="text-lg font-bold text-[color:var(--text-primary)]">{nomeDoVendedor(bloco.vendedor.nome) || `Vendedor ${bloco.vendedor.id}`}</h2>
      {selo}
      {bloco.estado !== 'fechada' && bloco.estado !== 'sem_acesso' && bloco.lido_em && !bloco.desatualizado && (
        <span className="text-xs text-[color:var(--text-muted)]">{quandoFoiLido(bloco.lido_em)}</span>
      )}
      {acesso && <Acesso vendedor={bloco.vendedor} {...acesso} />}
    </div>
  );

  if (bloco.estado === 'sem_acesso') {
    return (
      <div className="space-y-4">
        {cabecalho(null)}
        <p className={`${caixa} p-6 text-sm text-[color:var(--text-muted)]`}>{MOTIVOS[bloco.motivo] || 'Não há dados para mostrar.'}</p>
      </div>
    );
  }

  const { resumo, vendas } = bloco;
  const fechado = bloco.estado === 'fechada';
  const vendasFiltradas = vendas.filter((item) => correspondeABusca(item, busca));
  const preContratosFiltrados = bloco.pre_contratos.filter((item) => correspondeABusca(item, busca));
  const canceladasFiltradas = bloco.canceladas.filter((item) => correspondeABusca(item, busca));
  const upsellsFiltrados = bloco.upsells.filter((item) => correspondeABusca(item, busca));
  const buscando = Boolean(busca.trim());
  const instaladas = vendas.filter((venda) => venda.instalado === true).length;
  const vendido = vendas.reduce((total, venda) => total + Number(venda.valor || 0), 0);
  // Prévia é o estado de quase todo dia do mês: neutro, para o âmbar ficar só no aviso do IXC.
  const selo = (
    <span className={`rounded-full border px-3 py-1 text-xs font-semibold ${fechado
      ? 'border-[color:var(--status-good-border)] bg-[color:var(--status-good-chip)] text-[color:var(--status-good-strong)]'
      : 'border-[color:var(--border)] bg-[color:var(--surface-muted)] text-[color:var(--text-primary)]'}`}
    >
      {fechado && `Fechado · revisão ${bloco.revisao}`}
      {!fechado && (resumo ? 'Prévia: pode mudar até o fechamento' : 'Meta do mês ainda não definida')}
    </span>
  );

  return (
    <div className="space-y-4">
      {cabecalho(selo)}

      {(bloco.desatualizado || bloco.ixc_indisponivel) && (
        <p role="status" className={`flex items-start gap-2 rounded-lg border p-3 text-sm ${AVISO}`}>
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
          <span className="flex-1">
            {bloco.desatualizado
              ? `O IXC está indisponível: estes são os dados de ${diaEHora(bloco.lido_em)}.`
              : 'O IXC está indisponível: os valores estão corretos, mas faltam o nome dos clientes e o estado de instalação.'}
          </span>
          <button type="button" onClick={onTentarDeNovo} className="shrink-0 rounded font-semibold underline underline-offset-4 focus:outline-none focus:ring-2 focus:ring-current">Tentar de novo</button>
        </p>
      )}

      {bloco.meta_diaria_indisponivel && (
        <p role="status" className={`rounded-lg border p-3 text-sm ${AVISO}`}>
          A meta diária está indisponível no momento. Tente atualizar mais tarde.
        </p>
      )}

      {resumo ? (
        <Regua resumo={resumo} faixas={bloco.faixas || []} simulacao={bloco.simulacao} fechado={fechado} metaDiaria={bloco.meta_diaria} />
      ) : (
        <section className={`${caixa} p-5`}>
          <p className="text-[color:var(--text-primary)]">
            <span className="text-2xl font-bold tabular-nums">{vendas.length}</span>
            <span className="text-sm"> {vendas.length === 1 ? 'venda' : 'vendas'} no mês, {reais(vendido)} vendidos</span>
          </p>
          <p className="mt-2 text-sm text-[color:var(--text-muted)]">
            A meta deste mês ainda não foi definida. A faixa e a comissão aparecem assim que o gestor configurar a equipe do mês.
          </p>
        </section>
      )}

      {!buscando && <GraficoMetaDiaria metaDiaria={bloco.meta_diaria} />}

      {(!buscando || vendasFiltradas.length > 0) && <Contratos
        titulo="Vendas"
        nota={`${instaladas} de ${vendas.length} instaladas. A comissão é sobre o vendido; a instalação é só para acompanhar.`}
        contratos={vendasFiltradas}
        comComissao={Boolean(resumo)}
        buscando={buscando}
      />}

      {bloco.upsells.length > 0 && (!buscando || upsellsFiltrados.length > 0) && (
        <Recolhivel titulo={`Up-sells (${upsellsFiltrados.length})`}>
          <ul>
            {upsellsFiltrados.map((upsell) => (
              <li key={upsell.os_id} className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1 border-t border-[color:var(--border)] px-4 py-3 text-sm first:border-t-0">
                <span>
                  <span className="font-medium text-[color:var(--text-primary)]">{upsell.cliente_nome ? nomeParaCena(upsell.cliente_nome) : `Cliente ${upsell.cliente_id}`}</span>
                  <span className="block text-xs tabular-nums text-[color:var(--text-muted)]">OS {upsell.os_id}, {dia(upsell.data)}</span>
                </span>
                <span className="font-semibold tabular-nums text-[color:var(--text-primary)]">{reais(upsell.valor_comissao)}</span>
              </li>
            ))}
          </ul>
        </Recolhivel>
      )}

      {!fechado && bloco.pre_contratos.length > 0 && (!buscando || preContratosFiltrados.length > 0) && (
        <Contratos titulo="Pré-contratos" nota="Venda que ainda não conta: passa a contar quando o contrato for ativado." contratos={preContratosFiltrados} comComissao={false} buscando={buscando} />
      )}
      {!fechado && bloco.canceladas.length > 0 && (!buscando || canceladasFiltradas.length > 0) && (
        <Contratos titulo="Canceladas" nota="Venda do mês cujo contrato foi cancelado ou teve desistência. Não conta." contratos={canceladasFiltradas} comComissao={false} buscando={buscando} />
      )}
    </div>
  );
};

const MinhasVendasTab = () => {
  const { hasPermission } = usePermissions();
  const supervisor = hasPermission('comercial.view');
  const podeVincular = supervisor && hasPermission('comercial.metas.edit');

  const [mes, setMes] = useState(() => mesCorrente());
  const [internos, setInternos] = useState([]);
  const [vendedorId, setVendedorId] = useState(null);
  const [usuarios, setUsuarios] = useState([]);
  const [dados, setDados] = useState(null);
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState('');
  const [baixando, setBaixando] = useState('');
  const [busca, setBusca] = useState('');
  const avisoBuscaRef = useRef(null);
  const resultadosBuscaRef = useRef(null);

  // O supervisor escolhe entre os vendedores internos da competência; a vendedora não escolhe nada.
  useEffect(() => {
    if (!supervisor) return undefined;
    let vigente = true;

    service.competencia(mes)
      .then((competencia) => {
        if (!vigente) return;
        const lista = (competencia?.participantes || [])
          .filter((participante) => participante.tipo === 'interno')
          .sort((a, b) => String(a.vendedor_nome).localeCompare(String(b.vendedor_nome), 'pt-BR'));
        setInternos(lista);
        setVendedorId((atual) => (lista.some((item) => item.vendedor_id === atual) ? atual : lista[0]?.vendedor_id ?? null));
      })
      .catch(() => { if (vigente) setInternos([]); });

    return () => { vigente = false; };
  }, [supervisor, mes]);

  useEffect(() => {
    if (!podeVincular) return;
    service.usuariosParaVinculo().then((lista) => setUsuarios(lista || [])).catch(() => setUsuarios([]));
  }, [podeVincular]);

  const carregar = useCallback(async (atualizar = false) => {
    // O supervisor sem vendedor escolhido ainda não tem o que pedir.
    if (supervisor && !vendedorId) {
      setDados(null);
      setCarregando(false);
      return;
    }
    setCarregando(true);
    setErro('');
    try {
      setDados(await service.minhasVendas(mes, supervisor ? vendedorId : undefined, atualizar));
    } catch (falha) {
      setDados(null);
      setErro(falha.response?.data?.message || 'Não foi possível carregar as vendas.');
    } finally {
      setCarregando(false);
    }
  }, [mes, supervisor, vendedorId]);

  useEffect(() => { carregar(); }, [carregar]);

  const baixar = async (formato) => {
    setBaixando(formato);
    setErro('');
    try {
      const { blob, nome } = await service.exportarMinhasVendas(mes, formato, supervisor ? vendedorId : undefined);
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url; link.download = nome;
      document.body.appendChild(link); link.click(); link.remove();
      // Revogar na hora cancela o download em alguns navegadores; o quadro seguinte já é seguro.
      requestAnimationFrame(() => URL.revokeObjectURL(url));
    } catch (falha) {
      setErro('Não foi possível baixar o arquivo.');
    } finally {
      setBaixando('');
    }
  };

  const blocos = dados?.blocos || [];
  const semResultados = Boolean(busca.trim()) && !carregando && !erro && blocos.some((bloco) => bloco.estado !== 'sem_acesso')
    && !blocos.some((bloco) => (
      bloco.estado !== 'sem_acesso' && [bloco.vendas, bloco.upsells, ...(bloco.estado === 'fechada' ? [] : [bloco.pre_contratos, bloco.canceladas])]
        .some((lista) => lista?.some((item) => correspondeABusca(item, busca)))
    ));
  useEffect(() => {
    const destino = semResultados ? avisoBuscaRef.current : resultadosBuscaRef.current;
    if (busca.trim() && destino?.getBoundingClientRect().top < 0) {
      destino.scrollIntoView({ block: 'start' });
    }
  }, [busca, semResultados]);
  const exportavel = blocos.some((bloco) => bloco.estado !== 'sem_acesso');
  const opcoesDeVendedor = useMemo(
    () => internos.map((item) => ({ id: item.vendedor_id, label: nomeDoVendedor(item.vendedor_nome) })),
    [internos],
  );
  const opcoesDeUsuario = useMemo(() => usuarios.map((usuario) => ({ id: usuario.id, label: usuario.nome })), [usuarios]);

  // O erro aparece aqui em cima e é relançado: quem pediu não pode anunciar que gravou.
  const gravarVinculo = async (acao) => {
    setErro('');
    try {
      await acao();
      await carregar();
    } catch (falha) {
      setErro(falha.response?.data?.message || 'Não foi possível alterar o acesso.');
      throw falha;
    }
  };
  const acessoDe = (vendedor) => (podeVincular ? {
    usuarios: opcoesDeUsuario,
    onVincular: (userId) => gravarVinculo(() => service.vincularUsuario(
      vendedor.id,
      userId,
      vendedor.nome || internos.find((item) => item.vendedor_id === vendedor.id)?.vendedor_nome,
    )),
    onDesvincular: () => gravarVinculo(() => service.desvincularUsuario(vendedor.id)),
  } : null);

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-end gap-3">
        <label className="flex flex-col gap-1 text-xs font-medium text-[color:var(--text-muted)]">
          Mês
          <input type="month" value={mes} min={PRIMEIRO_MES} max={mesCorrente()} onChange={(evento) => evento.target.value && setMes(evento.target.value)} className={campo} />
        </label>

        {/* Seletor com busca, e não <select> nativo: a lista do navegador ignora o tema. */}
        {supervisor && (
          <div className="flex w-full flex-col gap-1 text-xs font-medium text-[color:var(--text-muted)] sm:w-64">
            Vendedor
            <SearchableSelect
              options={opcoesDeVendedor}
              value={opcoesDeVendedor.find((opcao) => opcao.id === vendedorId) || null}
              onChange={(opcao) => opcao && setVendedorId(opcao.id)}
              placeholder={internos.length ? 'Buscar vendedor' : 'Nenhum vendedor interno neste mês'}
              ariaLabel="Vendedor"
              disabled={internos.length === 0}
            />
          </div>
        )}

        <label className="flex w-full flex-col gap-1 text-xs font-medium text-[color:var(--text-muted)] sm:w-80">
          Buscar nas vendas
          <span className="relative">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2" aria-hidden="true" />
            <input
              type="search"
              value={busca}
              onChange={(evento) => setBusca(evento.target.value)}
              placeholder="Nome, ID do cliente ou contrato"
              className={`${campo} w-full pl-9`}
            />
          </span>
        </label>

        {/* Lê o IXC de novo: quem acabou de lançar uma venda não espera os 5 minutos do cache. */}
        <button type="button" onClick={() => carregar(true)} disabled={carregando} className={botaoSecundario}>
          <RefreshCw className={`h-4 w-4 ${carregando ? 'animate-spin motion-reduce:animate-none' : ''}`} aria-hidden="true" />
          {carregando ? 'Atualizando' : 'Atualizar'}
        </button>

        <div className="flex gap-2 sm:ml-auto">
          <button type="button" onClick={() => baixar('csv')} disabled={!exportavel || Boolean(baixando)} className={botaoSecundario}>
            <Download className="h-4 w-4" aria-hidden="true" />
            Baixar CSV
          </button>
          <button type="button" onClick={() => baixar('pdf')} disabled={!exportavel || Boolean(baixando)} className={botaoSecundario}>
            <FileText className="h-4 w-4" aria-hidden="true" />
            Baixar PDF
          </button>
        </div>
      </div>

      {semResultados && (
        <div ref={avisoBuscaRef} role="status" className={`${caixa} scroll-mt-24 flex flex-wrap items-center justify-between gap-3 p-4`}>
          <p className="text-sm text-[color:var(--text-primary)]">
            Nenhum registro encontrado para “{busca.trim()}” nas vendas exibidas deste vendedor neste mês.
            <span className="block text-[color:var(--text-muted)]">Confira o nome ou o ID do cliente ou contrato.</span>
          </p>
          <button type="button" onClick={() => setBusca('')} className={botaoSecundario}>Limpar busca</button>
        </div>
      )}

      {erro && (
        <p role="alert" className={`flex items-start gap-2 rounded-lg border p-3 text-sm ${ERRO}`}>
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
          <span className="flex-1">{erro}</span>
          <button type="button" onClick={() => carregar(true)} className="shrink-0 rounded font-semibold underline underline-offset-4 focus:outline-none focus:ring-2 focus:ring-current">Tentar de novo</button>
        </p>
      )}

      {carregando && !dados && <div role="status" aria-label="Carregando as vendas" className="h-40 animate-pulse rounded-xl bg-[color:var(--surface-muted)]" />}

      {!carregando && !erro && blocos.length === 0 && (
        <p className={`${caixa} p-6 text-sm text-[color:var(--text-muted)]`}>
          {supervisor ? 'Escolha um vendedor interno para ver as vendas dele.' : MOTIVOS[dados?.motivo] || MOTIVOS.sem_vinculo}
        </p>
      )}

      {/* Ao recarregar, o que já está na tela fica, esmaecido: trocar tudo por um esqueleto fazia a
          página pular e apagava a confirmação de quem acabou de gravar um acesso. */}
      <div ref={resultadosBuscaRef} aria-busy={carregando} className={`scroll-mt-24 space-y-8 ${carregando ? 'opacity-60' : ''}`}>
        {blocos.map((bloco) => <Bloco key={bloco.vendedor.id} bloco={bloco} acesso={acessoDe(bloco.vendedor)} onTentarDeNovo={() => carregar(true)} busca={busca} />)}
      </div>
    </div>
  );
};

export default MinhasVendasTab;
